import React, { useEffect, useState } from 'react';
import styles from './styles.module.scss';
import { useDispatch } from 'react-redux';
import {
  setHeaderConfigsAction,
  setHeaderTitleAction
} from '../../../reactRedux/Actions/headerAction';
import { useHistory } from 'react-router-dom';
import AddCircleOutlineRoundedIcon from '@material-ui/icons/AddCircleOutlineRounded';
import {
  addContentAction,
  addFabAction,
  closeSliderDialogAction,
  openSliderDialogAction
} from '../../../reactRedux/Actions/showSliderDialogAction';
import UserSearchDialog from '../../_dialogContent/UserSearchDialog';
import classNames from 'classnames';
import { routes } from '../../../assets/routes';
import { toast } from 'react-toastify';
import Avatar from '@material-ui/core/Avatar';
import { Helmet } from 'react-helmet';
import useDesktopDisplay from '../../../hooks/useDesktopDisplay';
import { toggleBackButtonAction } from '../../../reactRedux/Actions/sideBarAction';
import PropTypes from 'prop-types';
import { useMutation } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { getOrganizationFeaturesRequest } from '../../../helpers/api';
import { sendFeedbackRequest, uploadFeedbackRequestAttachment } from './api';
import { TextField } from '@mui/material';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from '../../../i18n/LanguageProvider';
import { formatNumber } from '../../../i18n';

const FeedbackRequestCreate = (props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const isDesktop = useDesktopDisplay();

  const [user, setUser] = useState(props.location.state || {});
  const [note, setNote] = useState('');
  const [title, setTitle] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);

  const removeFile = (index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const sendFeedbackRequestMutation = useMutation(sendFeedbackRequest, {
    onSuccess: async (response) => {
      const feedbackId = response?.data?.id;
      if (attachedFiles.length && feedbackId) {
        try {
          await Promise.all(
            attachedFiles.map((file) =>
              uploadFeedbackRequestAttachment(feedbackId, file)
            )
          );
          toast.success(t('conversations.requestSent'));
          history.push(routes.FEEDBACKS);
        } catch (err) {
          toast.error(t('conversations.uploadFailed'));
        }
      } else {
        toast.success(t('conversations.requestSent'));
        history.push(routes.FEEDBACKS);
      }
    },
    onError(err) {
      toast.error(err.response?.data?.detail);
    }
  });

  const setHeader = (title) => dispatch(setHeaderTitleAction(title));
  const setHeaderConfigs = (configs) =>
    dispatch(setHeaderConfigsAction(configs));
  const openSliderDialog = () => dispatch(openSliderDialogAction());
  const closeDialog = () => dispatch(closeSliderDialogAction());
  const setDialogContent = (content) => dispatch(addContentAction(content));
  const setDialogFab = (fab) => dispatch(addFabAction(fab));
  const setSideBarBackButton = (show) => dispatch(toggleBackButtonAction(show));

  const { data: orgFeatures } = useQuery(
    ['organizationFeatures'],
    getOrganizationFeaturesRequest
  );

  const hasFeedbackTitle = orgFeatures?.data?.find(
    (feature) => feature.feature_name === 'feedback_title'
  )?.active;

  useEffect(() => {
    setSideBarBackButton(true);
    return () => setSideBarBackButton(false);
  }, []);

  useEffect(() => {
    setHeader(t('conversations.single'));
    setHeaderConfigs({ credits: false, exit: false, back: true, logo: false });
  }, []);

  const selectUser = (data) => () => {
    setUser(data);
    history.goBack();
  };

  const setDialog = () => {
    setDialogFab([
      {
        type: 'default',
        isClose: true,
        iconOnly: true,
        onClickFn: closeDialog
      }
    ]);
    setDialogContent(<UserSearchDialog selectUsers={selectUser} />);
    openSliderDialog();
  };

  return (
    <div className={classNames(styles.page, styles.full)}>
      <Helmet>
        <title>{t('conversations.single')}</title>
      </Helmet>
      {isDesktop && (
        <div className={styles.pageHeader}>{t('conversations.single')}</div>
      )}

      <div className={styles.section}>
        {isDesktop && (
          <div className={styles.sectionTop}>
            <h4>{t('conversations.startWith')}</h4>
          </div>
        )}
        <div className={styles.sectionBody}>
          <span className={styles.select} onClick={setDialog}>
            {Object.keys(user).length ? (
              <>
                <Avatar
                  alt={user.name}
                  src={user.img}
                  className={styles.small}
                />
                <span>{user.name}</span>
              </>
            ) : (
              <>
                <AddCircleOutlineRoundedIcon />
                <span>{t('conversations.selectColleague')}</span>
              </>
            )}
          </span>
        </div>
      </div>

      {hasFeedbackTitle && (
        <div className={styles.section}>
          <div className={styles.sectionTop}>
            <h4>{t('conversations.titleOptional')}</h4>
          </div>
          <div className={styles.sectionBody}>
            <TextField
              id="title-feedback"
              name="title-feedback"
              placeholder={t('conversations.titlePlaceholder')}
              className={styles.title}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionTop}>
          <h4>{t('conversations.message')}</h4>
        </div>
        <div className={styles.sectionBody}>
          <TextField
            id="note-feedback"
            name="note-feedback"
            placeholder={t('conversations.messagePlaceholder')}
            multiline
            hiddenLabel
            className={styles.note}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.btnWrapper}>
        {hasFeedbackTitle && (
          <div className={styles.attachSection}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              style={{ marginTop: '1rem' }}
            >
              {t('conversations.attach')}
              <input
                type="file"
                hidden
                multiple
                onChange={(e) => {
                  const selected = Array.from(e.target.files);
                  const total = attachedFiles.length + selected.length;
                  if (total > 3) {
                    toast.warn(
                      t('conversations.maxFiles', { max: formatNumber(3) })
                    );
                    return;
                  }
                  setAttachedFiles((prev) => [...prev, ...selected]);
                }}
              />
            </Button>
            {attachedFiles.length > 0 && (
              <div className={styles.attachPreview}>
                {attachedFiles.map((file, index) => (
                  <div key={index} className={styles.fileItem}>
                    <span>{file.name}</span>
                    <IconButton
                      size="small"
                      onClick={() => removeFile(index)}
                      aria-label={t('common.delete')}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <Button
          variant="contained"
          disabled={!user.id || !note || sendFeedbackRequestMutation.isLoading}
          onClick={() => {
            sendFeedbackRequestMutation.mutate({
              receiverId: user.id,
              message: note,
              title: title || null
            });
          }}
          fullWidth
        >
          {t('conversations.send')}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={history.goBack}
          style={{ marginTop: '1rem' }}
        >
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );
};

FeedbackRequestCreate.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    state: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.bool,
      PropTypes.number,
      PropTypes.object,
      PropTypes.string
    ])
  })
};

export default FeedbackRequestCreate;
